import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { API_BASE } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 30000;   // 30-second poll — cheap enough on the /unread-count
                         // dedicated endpoint; anything higher makes the bell
                         // feel stale to family members refreshing constantly.

/**
 * Header bell icon with unread badge + recent-notifications dropdown.
 *
 * - Polls `/api/notifications/unread-count` every 30s (also on
 *   window focus) so the badge stays honest without a websocket.
 * - Opening the dropdown fetches the freshest 20 rows.
 * - Clicking a row: optimistically marks it read → navigates to
 *   deeplink (falls back to `/` if the row somehow has none).
 * - "Mark all read" clears the whole bell.
 */
export default function NotificationBell() {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [rows, setRows] = useState(null);   // null = not yet loaded
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const abortRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnread(Number(data?.count || 0));
    } catch (_) { /* silent — this is a background poll */ }
  }, []);

  const fetchList = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications?limit=20`, {
        withCredentials: true,
        signal: ctrl.signal,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (_) { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    const t = setInterval(fetchUnread, POLL_MS);
    const onFocus = () => fetchUnread();
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(t);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchUnread]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  const markRead = async (ids) => {
    if (!ids || ids.length === 0) return;
    try {
      await axios.post(`${API}/notifications/mark-read`, { ids }, { withCredentials: true });
    } catch (_) { /* ignore */ }
    // Optimistic local update — the /unread-count poll will re-verify.
    setRows((prev) => (prev || []).map((r) => (ids.includes(r.id) ? { ...r, read: true } : r)));
    setUnread((n) => Math.max(0, n - ids.length));
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-read`, { all: true }, { withCredentials: true });
    } catch (_) { /* ignore */ }
    setRows((prev) => (prev || []).map((r) => ({ ...r, read: true })));
    setUnread(0);
  };

  const onClickRow = async (row) => {
    if (!row.read) await markRead([row.id]);
    setOpen(false);
    navigate(row.deeplink || '/');
  };

  const initials = (name) =>
    (name || 'K').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  const timeAgo = (iso) => {
    if (!iso) return '';
    const s = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
    if (s < 60)      return 'just now';
    if (s < 3600)    return `${Math.floor(s / 60)}m ago`;
    if (s < 86400)   return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800)  return `${Math.floor(s / 86400)}d ago`;
    return new Date(iso).toLocaleDateString();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild data-testid="notification-bell-trigger">
        <button
          type="button"
          aria-label="Open notifications"
          className="relative p-2 rounded-full hover:bg-stone-100 active:bg-stone-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          <Bell className="w-5 h-5 text-stone-700" />
          {unread > 0 && (
            <span
              data-testid="notification-bell-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-80 sm:w-96 rounded-2xl p-0 overflow-hidden"
        data-testid="notification-bell-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 bg-stone-50">
          <div className="font-semibold text-stone-800">Notifications</div>
          {unread > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="text-xs text-teal-700 hover:text-teal-900 font-medium"
              data-testid="notification-bell-mark-all"
            >
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-[420px] overflow-y-auto" data-testid="notification-bell-list">
          {rows === null || (loading && rows === null) ? (
            <div className="px-4 py-8 text-center text-sm text-stone-500">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-stone-500">
              You&apos;re all caught up.
            </div>
          ) : (
            rows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => onClickRow(r)}
                data-testid={`notification-bell-row-${r.id}`}
                className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-stone-100 hover:bg-stone-50 transition-colors ${r.read ? '' : 'bg-teal-50/40'}`}
              >
                <Avatar className="h-9 w-9 flex-none">
                  {r.actor_avatar && (
                    <AvatarImage src={`${API_BASE}/files/${r.actor_avatar}`} alt={r.actor_name || ''} className="object-cover" />
                  )}
                  <AvatarFallback className="bg-teal-600 text-white text-xs">
                    {initials(r.actor_name)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-stone-900 truncate">
                    <span className="font-medium">{r.title}</span>
                  </div>
                  {r.body && (
                    <div className="text-xs text-stone-600 line-clamp-2 mt-0.5">{r.body}</div>
                  )}
                  <div className="text-[11px] text-stone-400 mt-1">{timeAgo(r.created_at)}</div>
                </div>
                {!r.read && (
                  <span className="mt-1.5 w-2 h-2 rounded-full bg-teal-500 flex-none" aria-label="unread" />
                )}
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
